import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ConfirmationPrompt } from '../../../../../components/ConfirmationPrompt';
import {
  COMMENTSTATUS_APPROVED,
  COMMENTSTATUS_NOTAPPROVED,
  USERCAPACITY_COMPANY,
  USERCAPACITY_EMPLOYEE,
} from '../../../../../constants/api';
import { commentStatusByIdSelector } from '../../../../../features/apiFeatures/commentStatusSlice';
import {
  commentReplyModelSelector,
  createOrUpdateCommentReplyInitialize,
  isCommentReplyModelUpdatingSelector,
  isCommentWithIdBeingUpdatedSelector,
  setCommentReplyModel,
  updateCommentStatusInitialize,
} from '../../../../../features/componentFeatures/commentSectionSlice';
import {
  getAttr,
  getEntityId,
  getRelId,
} from '../../../../../features/helpers/apiHelpers';
import { formatDateForRender } from '../../../../../features/helpers/dateHelpers';
import { downloadFileFromUrl } from '../../../../../utilities/files';
import { mapFileContentToFileItems } from '../../../../../utilities/mapperFunctions';
import { FileList } from '../../FileList';
import { CommentFooter } from './comment/CommentFooter';
import { CommentMetaData } from './comment/CommentMetaData';
import { CommentResponse } from './comment/CommentResponse';
import { DeclineCommentResponse } from './comment/DeclineCommentResponse';
import { userHasCapacitySelector } from '../../../../../features/apiFeatures/userCapacitySlice';
import { getResponderInfo } from './comment/getResponderInfo';
import { commentDeclineInfoByIdSelector } from '../../../../../features/apiFeatures/commentDeclineInfoSlice';
import { getCommentDeclineInfo } from './comment/getDeclineInfo';

const Comment = ({
  commentContent,
  isCommentOwner,
  isEditable,
  onUpdateComment,
  onDeleteComment,
  showFooter,
  hearingDeadline,
}) => {
  const { t: translator } = useTranslation();
  const dispatch = useDispatch();
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);

  const [isDeclinePopupOpen, setIsDeclinePopupOpen] = useState(false);
  const [isSendDeclinePopupOpen, setIsSendDeclinePopupOpen] = useState(false);
  const [declineResponse, setDeclineResponse] = useState('');

  const commentId = getEntityId(commentContent.comment);

  const commentText = getAttr(commentContent.textContent, 'textContent') || '-';
  const commentFiles = commentContent.fileContent || [];
  const commentChildText = getAttr(
    commentContent.childTextContent,
    'textContent',
  );
  const files = commentFiles.map(mapFileContentToFileItems);

  const isEmployee = useSelector(
    userHasCapacitySelector(commentContent.commentOwner, USERCAPACITY_EMPLOYEE),
  );
  const isCompany = useSelector(
    userHasCapacitySelector(commentContent.commentOwner, USERCAPACITY_COMPANY),
  );

  const responderInfo = getResponderInfo(
    isEmployee,
    isCompany,
    commentContent.commentOwner,
    commentContent.company,
    translator('pages.hearingDetails.companyLabel'),
    translator('pages.hearingDetails.employeeLabel'),
    translator('pages.hearingDetails.citizenLabel'),
    translator('pages.hearingDetails.missingUserInfoMessage'),
  );

  const commentNumber = '#' + getAttr(commentContent.comment, 'number');

  const createdAt = getAttr(commentContent.comment, 'created');
  let timeOfCreation = '-';
  if (createdAt) {
    timeOfCreation = createdAt < hearingDeadline ? createdAt : hearingDeadline;
    timeOfCreation = formatDateForRender(timeOfCreation);
  }

  const onBehalfOf = getAttr(commentContent.comment, 'onBehalfOf');
  const containsSensitiveInformation = getAttr(
    commentContent.comment,
    'containsSensitiveInformation',
  );

  const commentStatusId = getRelId(commentContent.comment, 'commentStatus');
  const commentStatus = useSelector(commentStatusByIdSelector(commentStatusId));
  const status = getAttr(commentStatus, 'status');

  const commentReplyModel = useSelector(commentReplyModelSelector(commentId));
  const isCommentReplyModelUpdating = useSelector(
    isCommentReplyModelUpdatingSelector,
  );

  const isCommentBeingUpdated = useSelector(
    isCommentWithIdBeingUpdatedSelector(commentId),
  );

  const onDownloadFile = (value) =>
    value && downloadFileFromUrl(value.urlToDownload, value.fileName);

  const onDeleteCommentLocal = () => {
    onDeleteComment(commentContent.id);
    setIsDeletePopupOpen(false);
  };

  const updateCommentWithNewStatus = (status, declineReason = null) =>
    dispatch(
      updateCommentStatusInitialize({
        hearingId: commentContent.hearingId,
        commentId: commentContent.id,
        status,
        declineReason,
      }),
    );

  const commentDeclineInfoId = getRelId(
    commentContent.comment,
    'commentDeclineInfo',
  );
  const commentDeclineInfo = useSelector(
    commentDeclineInfoByIdSelector(commentDeclineInfoId),
  );

  const isCommentDeclined = status === COMMENTSTATUS_NOTAPPROVED;
  const { commentDeclineReason, commentDecliner } =
    isCommentDeclined && !!commentDeclineInfoId
      ? getCommentDeclineInfo(commentDeclineInfo)
      : { commentDeclineReason: declineResponse, commentDecliner: null };

  const isDeclineResponseOpen = isCommentDeclined || isDeclinePopupOpen;
  const hasDeclineReason = isCommentDeclined && !!commentDeclineReason;

  const isCommentReplyEdit = !!commentChildText;
  const commentHasReply = !!commentChildText || !!commentReplyModel;
  const isCommentReplyEditMode = !!commentReplyModel;
  const commentReplyText = isCommentReplyEditMode
    ? commentReplyModel.text
    : commentChildText;

  const onCommentReplyModelChange = (value) => {
    const newModel = Object.assign({}, commentReplyModel, { text: value });
    dispatch(setCommentReplyModel(newModel));
  };

  const onReplyToComment = () => {
    dispatch(
      setCommentReplyModel({
        hearingId: commentContent.hearingId,
        parrentCommentId: commentId,
        text: commentChildText || '',
        commentId: commentContent.childCommentId,
      }),
    );
  };

  const onApproveComment = () =>
    updateCommentWithNewStatus(COMMENTSTATUS_APPROVED);

  const onDeclineComment = () => setIsSendDeclinePopupOpen(true);

  const onSendDeclineResponse = () => {
    updateCommentWithNewStatus(
      COMMENTSTATUS_NOTAPPROVED,
      declineResponse.trim(),
    );
    setIsSendDeclinePopupOpen(false);
    setIsDeclinePopupOpen(false);
  };

  const cancelCreateReply = () => dispatch(setCommentReplyModel(null));
  const confirmCreateReply = () =>
    dispatch(createOrUpdateCommentReplyInitialize(commentReplyModel));

  const textLines = commentText.split('\r\n');

  return (
    <div>
      <div
        className={
          'hearing-response-box' + (commentHasReply ? '--has-reply' : '')
        }
        tabIndex="0"
        aria-label={translator('ariaLabel.comment')}
      >
        {textLines.map((textLine) =>
          textLine === '' ? <br></br> : <p key={textLine}>{textLine}</p>,
        )}
        <CommentMetaData
          onBehalfOf={onBehalfOf}
          userInfo={responderInfo}
          commentNumber={commentNumber}
          timeOfCreation={timeOfCreation}
          onEditCommentClick={() => onUpdateComment(commentContent.id)}
          onDeleteCommentClick={() => setIsDeletePopupOpen(true)}
          disableDeleteComment={!isCommentOwner}
          disableEditComment={!isCommentOwner || !isEditable}
        />
        {files.length === 0 ? null : (
          <FileList
            onDownloadFile={onDownloadFile}
            items={files}
            isCompact={true}
            headerText={translator('pages.hearingDetails.appendix')}
          />
        )}

        <ConfirmationPrompt
          isOpen={isDeletePopupOpen}
          onDismiss={() => setIsDeletePopupOpen(false)}
          onConfirm={onDeleteCommentLocal}
          text={translator('pages.hearingDetails.deleteCommentPrompt')}
        />
        <ConfirmationPrompt
          isOpen={isSendDeclinePopupOpen}
          onDismiss={() => setIsSendDeclinePopupOpen(false)}
          onConfirm={onSendDeclineResponse}
          text={translator('pages.hearingDetails.declineCommentPrompt')}
        />
        <CommentFooter
          onApproveComment={onApproveComment}
          onDeclineComment={() => setIsDeclinePopupOpen(true)}
          onReplyToComment={onReplyToComment}
          commentStatus={commentStatus}
          containsSensitiveInformation={containsSensitiveInformation}
          showSpinner={isCommentBeingUpdated}
          showFooter={showFooter}
          isCommentReplyEdit={isCommentReplyEdit}
        />
      </div>
      <CommentResponse
        showCommentReply={commentHasReply}
        textValue={commentReplyText}
        onChangeText={onCommentReplyModelChange}
        isEditMode={isCommentReplyEditMode}
        cancelCreateReply={cancelCreateReply}
        confirmCreateReply={confirmCreateReply}
        showSpinner={isCommentReplyModelUpdating}
      />
      <DeclineCommentResponse
        isOpen={isDeclineResponseOpen}
        commentDeclineReason={commentDeclineReason}
        commentDecliner={commentDecliner}
        onChangeText={(value) => setDeclineResponse(value)}
        cancelDeclineComment={() => setIsDeclinePopupOpen(false)}
        confirmDeclineComment={onDeclineComment}
        isEditMode={!isCommentDeclined}
        hasDeclineReason={hasDeclineReason}
      />
    </div>
  );
};

export { Comment };
